import { useState, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { useProductsServices } from "@/hooks/useProductsServices";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Button } from "@/components/ui/button";
import { NewEstimateDialog } from "@/components/controller/NewEstimateDialog";
import type { Estimate } from "@/hooks/useEstimates";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DataTable } from "@/components/shared/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  AlertTriangle,
  Printer,
  Lock,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { handleRpcError } from "@/lib/rpcErrors";
import { differenceInDays } from "date-fns";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { exportPdf } from "@/lib/export";
import { ChangeOrderPrintTemplate } from "@/components/shared/print-templates/ChangeOrderPrintTemplate";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Database } from "@/integrations/supabase/types";
import { COLineItemsForm, type COLineItem, type COType, type SovLineOption, type VendorOption } from "./COLineItemsForm";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    n
  );

const LOCKED_STATUSES = ["approved", "rejected", "closed", "cancelled"];
const EDITABLE_STATUSES = ["draft", "submitted"];
const NONE_OPTION = "__none__";

type OrderStatus = Database["public"]["Enums"]["order_status"];
type FormLineItem = COLineItem & { persisted?: boolean };
type ChangeOrderLineRow = {
  id: string;
  line_number: number | string;
  description: string | null;
  quantity: number | string | null;
  unit: string | null;
  labor_unit_price: number | string | null;
  material_unit_price: number | string | null;
  unit_price: number | string | null;
  product_service_id: string | null;
  income_account: string | null;
  cogs_account: string | null;
};
type ChangeOrderWritePayload =
  Database["public"]["Tables"]["change_orders"]["Update"] & {
    notes: string | null;
  };
type ChangeOrderInsertPayload =
  Database["public"]["Tables"]["change_orders"]["Insert"] & {
    notes: string | null;
  };

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: string }).message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return fallback;
};

interface CO {
  id: string;
  number: string;
  description: string | null;
  notes: string | null;
  amount: number;
  status: OrderStatus;
  co_type?: COType;
  job_order_id: string | null;
  work_order_id: string | null;
  job_orders?: { number: string } | null;
  work_orders?: { number: string } | null;
  created_at: string;
}

const CO_TYPE_LABELS: Record<COType, string> = {
  addendum: "Addendum (Scope Increase)",
  deduction: "Deduction (Scope Decrease)",
  backcharge: "Back Charge",
  substitution: "Substitution (Swap)",
};

interface ProjectInfo {
  name: string;
  project_number: string | null;
  location: string | null;
  company_id: string | null;
  customer_id: string | null;
}

interface Props {
  projectId: string;
  project: ProjectInfo;
  cos: CO[];
  jos: { id: string; number: string }[];
  wos: { id: string; number: string }[];
  onMutate: () => void;
}

export const ProjectCOTab = ({
  projectId,
  project,
  cos,
  jos,
  wos,
  onMutate,
}: Props) => {
  const isMobile = useIsMobile();
  const navigate = useTabNavigate();
  const { data: companyProfile } = useCompanyProfile();
  const { data: productsData } = useProductsServices();
  const activeProducts = useMemo(() => productsData?.filter(p => p.is_active && p.status === "active") ?? [], [productsData]);
  const [open, setOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingCO, setEditingCO] = useState<CO | null>(null);
  const [loadedLineIds, setLoadedLineIds] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [coType, setCoType] = useState<COType>("addendum");
  const [jobOrderId, setJobOrderId] = useState(NONE_OPTION);
  const [workOrderId, setWorkOrderId] = useState(NONE_OPTION);
  const [lineItems, setLineItems] = useState<FormLineItem[]>([]);

  // Fetch SOV lines for deduction picker
  const { data: sovLinesData } = useQuery<SovLineOption[]>({
    queryKey: ["co_sov_lines", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sov_lines")
        .select("id, description, quantity, unit, billed_to_date, contract_id")
        .eq("contract_id", (await supabase.from("contracts").select("id").eq("project_id", projectId).order("created_at").limit(1).single()).data?.id ?? "")
        .order("line_number");
      if (error) throw error;
      return (data ?? []).map((s: any) => ({
        id: s.id,
        description: s.description ?? "",
        quantity: Number(s.quantity),
        unit: s.unit ?? "EA",
        billed_to_date: Number(s.billed_to_date ?? 0),
        remaining_qty: Number(s.quantity) - Number(s.billed_to_date ?? 0),
      }));
    },
    enabled: coType === "deduction" || coType === "substitution",
  });

  // Fetch vendors for backcharge picker
  const { data: vendorsData } = useQuery<VendorOption[]>({
    queryKey: ["co_vendors", project.company_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendors")
        .select("id, name")
        .eq("company_id", project.company_id!)
        .order("name");
      if (error) throw error;
      return (data ?? []).map((v: any) => ({ id: v.id, name: v.name }));
    },
    enabled: coType === "backcharge" && !!project.company_id,
  });

  // Fetch CO-estimates linked to this project
  const { data: coEstimates } = useQuery<Estimate[]>({
    queryKey: ["co_estimates", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*, customers(name)")
        .eq("project_id", projectId)
        .eq("is_change_order", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Estimate[];
    },
  });

  const staleCOs = cos.filter(
    (co) =>
      co.status === "submitted" &&
      differenceInDays(new Date(), new Date(co.created_at)) > 3
  );

  // Generate project-scoped CO number: CO-FL0004-01
  const generateCONumber = () => {
    const projNum = (project.project_number || "0000").replace(/-/g, "");
    const seq = (cos?.length ?? 0) + 1;
    return `CO-${projNum}-${String(seq).padStart(2, "0")}`;
  };

  const lineItemsTotal = lineItems.reduce(
    (sum, l) => sum + l.quantity * l.unit_price,
    0
  );

  const canEdit = (status: OrderStatus) => EDITABLE_STATUSES.includes(status);
  const canRevert = (status: OrderStatus) => status === "approved";

  const resetForm = () => {
    setEditingCO(null);
    setLoadedLineIds([]);
    setNotes("");
    setCoType("addendum");
    setJobOrderId(NONE_OPTION);
    setWorkOrderId(NONE_OPTION);
    setLineItems([]);
  };

  const closeDialog = () => {
    setOpen(false);
    resetForm();
  };

  const openCreateDialog = () => {
    resetForm();
    setOpen(true);
  };

  const normalizeLinkedId = (value: string) =>
    value === NONE_OPTION ? null : value || null;

  const buildLinePayload = (changeOrderId: string) =>
    lineItems.map((line, index) => ({
      id: line.id,
      change_order_id: changeOrderId,
      company_id: project.company_id,
      line_number: index + 1,
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unit: line.unit,
      unit_price: Number(line.unit_price),
      labor_unit_price: Number(line.labor_unit_price),
      material_unit_price: Number(line.material_unit_price),
      product_service_id: line.product_service_id || null,
      income_account: line.income_account || null,
      cogs_account: line.cogs_account || null,
      target_sov_line_id: line.target_sov_line_id || null,
      vendor_id: line.vendor_id || null,
      is_passthrough: line.is_passthrough ?? false,
    }));

  const loadChangeOrderLines = async (coId: string) => {
    const { data, error } = await (supabase as any)
      .from("change_order_lines")
      .select(
        "id, line_number, description, quantity, unit, labor_unit_price, material_unit_price, unit_price, product_service_id, income_account, cogs_account, target_sov_line_id, vendor_id, is_passthrough"
      )
      .eq("change_order_id", coId)
      .order("line_number");

    if (error) throw error;

    return ((data ?? []) as ChangeOrderLineRow[]).map((line) => ({
      id: line.id,
      line_number: Number(line.line_number),
      description: line.description ?? "",
      quantity: Number(line.quantity ?? 0),
      unit: line.unit ?? "SF",
      labor_unit_price: Number(line.labor_unit_price ?? 0),
      material_unit_price: Number(line.material_unit_price ?? 0),
      unit_price: Number(line.unit_price ?? 0),
      product_service_id: line.product_service_id ?? undefined,
      income_account: line.income_account ?? undefined,
      cogs_account: line.cogs_account ?? undefined,
      target_sov_line_id: (line as any).target_sov_line_id ?? undefined,
      vendor_id: (line as any).vendor_id ?? undefined,
      is_passthrough: (line as any).is_passthrough ?? false,
      persisted: true,
    }));
  };

  const openEditDialog = async (co: CO) => {
    if (!canEdit(co.status)) {
      toast.error("Only draft or submitted change orders can be edited");
      return;
    }

    setBusy(co.id);
    try {
      const loadedLines = await loadChangeOrderLines(co.id);
      setEditingCO(co);
      setLoadedLineIds(loadedLines.map((line) => line.id));
      setNotes(co.notes ?? "");
      setCoType(co.co_type ?? "addendum");
      setJobOrderId(co.job_order_id ?? NONE_OPTION);
      setWorkOrderId(co.work_order_id ?? NONE_OPTION);
      setLineItems(loadedLines);
      setOpen(true);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to load change order"));
    } finally {
      setBusy(null);
    }
  };

  const handleRowAction = async (co: CO, actionId: string) => {
    if (actionId === "print" || actionId === "export_pdf") {
      exportPdf(
        <ChangeOrderPrintTemplate data={getPrintData(co)} />,
        `Change Order ${co.number}`
      );
      return;
    }

    if (actionId === "approve") {
      if (co.status !== "submitted") {
        toast.error("Only submitted change orders can be approved");
        return;
      }
      await transition(co.id, "approved", `Change Order ${co.number} approved`);
      return;
    }

    if (actionId === "edit") {
      await openEditDialog(co);
      return;
    }

    if (actionId === "revert") {
      if (!canRevert(co.status)) {
        toast.error("Only approved change orders can be reverted");
        return;
      }
      await transition(
        co.id,
        "draft",
        `Change Order ${co.number} reverted to draft`
      );
    }
  };

  const transition = async (
    id: string,
    newStatus: OrderStatus,
    successMessage?: string
  ) => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc("transition_change_order_status", {
        p_co_id: id,
        p_new_status: newStatus,
      });
      if (error) return handleRpcError(error);
      toast.success(successMessage ?? `CO ${newStatus}`);
      onMutate();
    } finally {
      setBusy(null);
    }
  };

  const saveChangeOrder = async () => {
    if (lineItems.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    if (lineItems.some((l) => !l.description.trim())) {
      toast.error("All line items need a description");
      return;
    }

    if (!project.company_id) {
      toast.error("Project company is missing. Unable to save change order.");
      return;
    }

    const busyKey = editingCO?.id ?? "new";
    const payload = {
      description: lineItems.map((line) => line.description.trim()).join("; "),
      notes: notes.trim() || null,
      amount: lineItemsTotal,
      co_type: coType,
      job_order_id: normalizeLinkedId(jobOrderId),
      work_order_id: normalizeLinkedId(workOrderId),
    };

    setBusy(busyKey);
    try {
      if (editingCO) {
        const { error: updateError } = await supabase
          .from("change_orders")
          .update(payload)
          .eq("id", editingCO.id);
        if (updateError) throw updateError;

        const { error: upsertError } = await (supabase as any)
          .from("change_order_lines")
          .upsert(buildLinePayload(editingCO.id), { onConflict: "id" });
        if (upsertError) throw upsertError;

        const removedLineIds = loadedLineIds.filter(
          (lineId) => !lineItems.some((line) => line.id === lineId)
        );
        if (removedLineIds.length > 0) {
          const { error: deleteError } = await (supabase as any)
            .from("change_order_lines")
            .delete()
            .eq("change_order_id", editingCO.id)
            .in("id", removedLineIds);
          if (deleteError) throw deleteError;
        }

        toast.success(`Change Order ${editingCO.number} updated`);
      } else {
        const coNumber = generateCONumber();
        const createPayload: ChangeOrderInsertPayload = {
          project_id: projectId,
          number: coNumber,
          ...payload,
        };
        const { data: newCO, error: createError } = await supabase
          .from("change_orders")
          .insert(createPayload)
          .select("id")
          .single();
        if (createError) throw createError;

        const { error: linesError } = await (supabase as any)
          .from("change_order_lines")
          .insert(buildLinePayload(newCO.id));
        if (linesError) throw linesError;

        toast.success(`Change Order ${coNumber} created`);
      }

      closeDialog();
      onMutate();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to save change order"));
    } finally {
      setBusy(null);
    }
  };

  const isLocked = (status: OrderStatus) => LOCKED_STATUSES.includes(status);

  const getPrintData = (co: CO) => ({
    number: co.number,
    description: co.description,
    notes: co.notes ?? undefined,
    amount: Number(co.amount),
    status: co.status,
    created_at: co.created_at,
    project_name: project.name,
    project_number: project.project_number || undefined,
    company_name: companyProfile?.name,
    company_logo_url: companyProfile?.logo_url,
    job_order_number: co.job_orders?.number,
    work_order_number: co.work_orders?.number,
  });

  return (
    <div className="space-y-4">
      {staleCOs.length > 0 && (
        <Alert className="bg-yellow-50 border-yellow-300 dark:bg-yellow-950 dark:border-yellow-800">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
            {staleCOs.length} change order(s) pending approval for more than 3
            days.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        {cos.length > 0 && (
          <ExportToolbar entityType="change_orders" data={cos} />
        )}
        <Button size="sm" onClick={() => setEstimateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New CO
        </Button>
      </div>

      {/* CO-Estimates Section */}
      {(coEstimates ?? []).length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">CO Estimates</h3>
          <div className="border rounded-lg divide-y">
            {(coEstimates ?? []).map((est) => (
              <div
                key={est.id}
                className="flex items-center justify-between p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => navigate(`/controller/estimates/${est.id}`)}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-mono text-sm font-medium">
                    {est.estimate_number || "Draft"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {est.notes?.slice(0, 60) || "Change Order Estimate"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">
                    {fmt(Number(est.total_amount))}
                  </span>
                  <StatusBadge status={est.status === "Draft" || est.status === "Sent" ? "pending" : est.status.toLowerCase()} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <NewEstimateDialog
        open={estimateOpen}
        onOpenChange={setEstimateOpen}
        defaultCustomerId={project.customer_id ?? undefined}
        projectContext={{
          projectId,
          projectName: project.name,
          projectNumber: project.project_number,
        }}
      />

      {!cos.length && !(coEstimates ?? []).length ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-2">No change orders yet</p>
          <Button variant="outline" size="sm" onClick={() => setEstimateOpen(true)}>
            Create your first CO
          </Button>
        </div>
      ) : !cos.length ? null : isMobile ? (
        /* ── Mobile Card View ── */
        <div className="space-y-3">
          {cos.map((co) => (
            <EntityContextMenu
              key={co.id}
              entityType="change_orders"
              entityId={co.id}
              data={co}
              onAction={(actionId) => {
                void handleRowAction(co, actionId);
              }}
            >
              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-sm">
                    {co.number}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {isLocked(co.status) && (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <StatusBadge status={co.status} />
                  </div>
                </div>
                {co.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {co.description}
                  </p>
                )}
                <div className="text-lg font-bold">
                  {fmt(Number(co.amount))}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {co.job_orders?.number && (
                    <span>JO: {co.job_orders.number}</span>
                  )}
                  {co.work_orders?.number && (
                    <span>WO: {co.work_orders.number}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() =>
                      exportPdf(
                        <ChangeOrderPrintTemplate data={getPrintData(co)} />,
                        `Change Order ${co.number}`
                      )
                    }
                  >
                    <Printer className="h-3.5 w-3.5" />
                  </Button>
                  {canEdit(co.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      disabled={!!busy}
                      onClick={() => void openEditDialog(co)}
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  )}
                  {canRevert(co.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      disabled={!!busy}
                      onClick={() =>
                        void transition(
                          co.id,
                          "draft",
                          `Change Order ${co.number} reverted to draft`
                        )
                      }
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      Revert
                    </Button>
                  )}
                  {co.status === "submitted" && (
                    <>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 h-7 text-xs flex-1"
                        disabled={!!busy}
                        onClick={() =>
                          void transition(
                            co.id,
                            "approved",
                            `Change Order ${co.number} approved`
                          )
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs flex-1"
                        disabled={!!busy}
                        onClick={() =>
                          void transition(
                            co.id,
                            "rejected",
                            `Change Order ${co.number} rejected`
                          )
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {co.status === "draft" && (
                    <Button
                      size="sm"
                      className="h-7 text-xs flex-1"
                      disabled={!!busy}
                      onClick={() =>
                        void transition(
                          co.id,
                          "submitted",
                          `Change Order ${co.number} submitted`
                        )
                      }
                    >
                      Submit
                    </Button>
                  )}
                </div>
              </div>
            </EntityContextMenu>
          ))}
        </div>
      ) : (
        /* ── Desktop Table View ── */
        <CODesktopTable
          cos={cos}
          busy={busy}
          isLocked={isLocked}
          canEdit={canEdit}
          canRevert={canRevert}
          openEditDialog={openEditDialog}
          transition={transition}
          getPrintData={getPrintData}
          handleRowAction={handleRowAction}
        />
      )}

      {/* ── Create CO Dialog ── */}
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => (nextOpen ? setOpen(true) : closeDialog())}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCO
                ? `Edit Change Order ${editingCO.number}`
                : "New Change Order"}
            </DialogTitle>
            <DialogDescription>
              {editingCO
                ? "Update the change order scope, linked orders, and pricing before approval."
                : "Create a new CO for this project. Add line items to define the scope and pricing."}
            </DialogDescription>
          </DialogHeader>

          {/* Auto-populated project info */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-md text-sm">
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Project
              </span>
              <p className="font-medium">{project.name}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Project #
              </span>
              <p className="font-medium font-mono">
                {project.project_number || "—"}
              </p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Location
              </span>
              <p className="font-medium">{project.location || "—"}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                CO Number
              </span>
              <p className="font-medium font-mono">
                {editingCO?.number ?? generateCONumber()}
              </p>
            </div>
          </div>

          {/* CO Type selector */}
          <div className="space-y-2">
            <Label>Change Order Type</Label>
            <Select value={coType} onValueChange={(v) => setCoType(v as COType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CO_TYPE_LABELS) as COType[]).map((t) => (
                  <SelectItem key={t} value={t}>{CO_TYPE_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Line items */}
          <COLineItemsForm
            lines={lineItems}
            onChange={setLineItems}
            products={activeProducts}
            coType={coType}
            sovLines={sovLinesData}
            vendors={vendorsData}
          />

          {/* Total display */}
          {lineItems.length > 0 && (
            <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
              <span className="text-sm font-medium">
                Total Change Order Amount
              </span>
              <span className="text-xl font-bold font-mono">
                {fmt(lineItemsTotal)}
              </span>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this change order..."
              rows={2}
            />
          </div>

          {/* Link to JO / WO */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Link to Job Order (optional)</Label>
              <Select value={jobOrderId} onValueChange={setJobOrderId}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_OPTION}>None</SelectItem>
                  {jos.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Link to Work Order (optional)</Label>
              <Select value={workOrderId} onValueChange={setWorkOrderId}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_OPTION}>None</SelectItem>
                  {wos.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              disabled={!!busy || lineItems.length === 0}
              onClick={saveChangeOrder}
            >
              {busy === (editingCO?.id ?? "new")
                ? "Saving…"
                : editingCO
                ? "Save Changes"
                : "Create CO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Desktop DataTable wrapper ─────────────────────────────────────────

function CODesktopTable({
  cos,
  busy,
  isLocked,
  canEdit,
  canRevert,
  openEditDialog,
  transition,
  getPrintData,
  handleRowAction,
}: {
  cos: CO[];
  busy: string | null;
  isLocked: (s: OrderStatus) => boolean;
  canEdit: (s: OrderStatus) => boolean;
  canRevert: (s: OrderStatus) => boolean;
  openEditDialog: (co: CO) => void;
  transition: (id: string, status: OrderStatus, msg?: string) => Promise<any>;
  getPrintData: (co: CO) => any;
  handleRowAction: (co: CO, actionId: string) => Promise<void>;
}) {
  const columns = useMemo<ColumnDef<CO, any>[]>(
    () => [
      {
        accessorKey: "number",
        header: "CO #",
        cell: ({ row }) => (
          <span className="font-medium font-mono">{row.original.number}</span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => row.original.description || "—",
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <span className="text-right block">{fmt(Number(row.original.amount))}</span>
        ),
        meta: { className: "text-right" },
      },
      {
        id: "linked_jo",
        accessorFn: (row) => row.job_orders?.number ?? "",
        header: "Linked JO",
        cell: ({ row }) => row.original.job_orders?.number ?? "—",
      },
      {
        id: "linked_wo",
        accessorFn: (row) => row.work_orders?.number ?? "",
        header: "Linked WO",
        cell: ({ row }) => row.original.work_orders?.number ?? "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            {isLocked(row.original.status) && (
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <StatusBadge status={row.original.status} />
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }) => {
          const co = row.original;
          return (
            <div className="flex justify-end gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                title="Print CO"
                onClick={() =>
                  exportPdf(
                    <ChangeOrderPrintTemplate data={getPrintData(co)} />,
                    `Change Order ${co.number}`
                  )
                }
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
              {canEdit(co.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={!!busy}
                  onClick={() => void openEditDialog(co)}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              {canRevert(co.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={!!busy}
                  onClick={() =>
                    void transition(co.id, "draft", `Change Order ${co.number} reverted to draft`)
                  }
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                  Revert
                </Button>
              )}
              {co.status === "draft" && (
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  disabled={!!busy}
                  onClick={() =>
                    void transition(co.id, "submitted", `Change Order ${co.number} submitted`)
                  }
                >
                  Submit
                </Button>
              )}
              {co.status === "submitted" && (
                <>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                    disabled={!!busy}
                    onClick={() =>
                      void transition(co.id, "approved", `Change Order ${co.number} approved`)
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 text-xs"
                    disabled={!!busy}
                    onClick={() =>
                      void transition(co.id, "rejected", `Change Order ${co.number} rejected`)
                    }
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [busy, isLocked, canEdit, canRevert, openEditDialog, transition, getPrintData]
  );

  return (
    <DataTable<CO>
      tableId="project-co-list"
      columns={columns}
      data={cos}
      emptyMessage="No change orders found."
      enableFiltering={false}
      enableColumnToggle={false}
      renderRowWrapper={(row, children) => (
        <EntityContextMenu
          entityType="change_orders"
          entityId={row.id}
          data={row}
          onAction={(actionId) => {
            void handleRowAction(row, actionId);
          }}
        >
          {children}
        </EntityContextMenu>
      )}
    />
  );
}
